import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentResult = ({ kind }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState(kind === "success" ? "checking" : "cancelled");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [gcrFallback, setGcrFallback] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBuyerEmail(data.email || "");
          setState("paid");
          return;
        }
      } catch {
        /* keep polling briefly */
      }
      polls.current += 1;
      if (polls.current < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google Customer Reviews opt-in — offers the review survey after a paid order
  useEffect(() => {
    if (state !== "paid" || !buyerEmail || !sessionId) return;
    // estimated delivery: +10 days covers the 3-8 business day AusPost window
    const eta = new Date(Date.now() + 10 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const renderSurvey = () => {
      if (!window.gapi || !window.gapi.load) return;
      window.gapi.load("surveyoptin", function () {
        window.gapi.surveyoptin.render({
          merchant_id: 5872311518,
          order_id: sessionId,
          email: buyerEmail,
          delivery_country: "AU",
          estimated_delivery_date: eta,
        });
      });
    };
    window.renderOptIn = renderSurvey;

    const existing = document.querySelector('script[src^="https://apis.google.com/js/platform.js"]');
    if (existing) {
      // Script already on the page (repeat visit) — fire the dialog again directly
      renderSurvey();
    } else {
      const s = document.createElement("script");
      s.src = "https://apis.google.com/js/platform.js?onload=renderOptIn";
      s.async = true;
      s.defer = true;
      s.onerror = () => setGcrFallback(true);
      document.body.appendChild(s);
    }

    // If Google's dialog never mounts (blocked by an extension or suppressed by
    // Google for this user), reveal the in-page fallback link instead
    const timer = setTimeout(() => {
      if (!document.querySelector('iframe[src*="google"]')) setGcrFallback(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [state, buyerEmail, sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope grid-lines flex items-center justify-center px-6">
      <div className="w-full max-w-lg border border-white/10 bg-[#121212] p-8 md:p-12 text-left">
        <img src="/breethe-logo.webp" alt="Breethe" className="h-7 w-auto" />

        {kind === "cancel" ? (
          <div data-testid="payment-cancel-page">
            <p className="mt-8 font-plex text-xs uppercase tracking-[0.25em] text-white/40">Checkout</p>
            <h1 className="mt-3 font-anton uppercase text-4xl md:text-5xl leading-[0.9]">
              Cancelled<span className="text-volt">.</span>
            </h1>
            <p className="mt-4 text-white/60 font-light">
              No charge, no hard feelings. The pack will be waiting when you're ready.
            </p>
            <a
              href="/#pricing"
              data-testid="payment-retry-link"
              className="mt-8 inline-block bg-volt text-black font-anton uppercase tracking-wider text-base px-8 py-4 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
            >
              Back to Pricing
            </a>
          </div>
        ) : (
          <div data-testid="payment-success-page">
            <p className="mt-8 font-plex text-xs uppercase tracking-[0.25em] text-volt">N°001 — Order</p>
            <h1 className="mt-3 font-anton uppercase text-4xl md:text-5xl leading-[0.9]">
              {state === "paid" ? (
                <>Order locked in<span className="text-volt">.</span></>
              ) : state === "pending" ? (
                <>Processing<span className="text-volt">…</span></>
              ) : (
                <>Confirming<span className="text-volt">…</span></>
              )}
            </h1>
            <p data-testid="payment-status-text" className="mt-4 text-white/60 font-light">
              {state === "paid"
                ? "Payment confirmed. Your strips ship from the first run — watch your inbox for confirmation."
                : state === "pending"
                  ? "Payment is taking a moment to confirm. If you were charged, you're in — check your email."
                  : "Talking to the payment gods. Hold tight."}
            </p>
            {state === "paid" && (
              <a
                href="/"
                data-testid="payment-home-link"
                className="mt-8 inline-block bg-volt text-black font-anton uppercase tracking-wider text-base px-8 py-4 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95"
              >
                Back to Breethe
              </a>
            )}
            {state === "paid" && gcrFallback && (
              <p data-testid="review-fallback" className="mt-6 text-xs text-white/40 font-light">
                Loving Breethe already?{" "}
                <a
                  href="mailto:info@breethe.com.au?subject=My%20Breethe%20review"
                  data-testid="review-fallback-link"
                  className="underline decoration-white/30 underline-offset-2 transition-colors duration-200 hover:text-white/70"
                >
                  Leave us a review
                </a>{" "}
                — or keep an eye on your inbox after your order arrives.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;
